import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../hooks/useToast';
import { FilePersistenceService } from '../../services/FilePersistenceService';
import { JSONPersistenceService } from '../../services/JSONPersistenceService';

interface ImportExportPanelProps {
  onImportComplete?: (importedCount: number) => void;
  onRefresh?: () => void;
}

interface ImportOptions {
  overwriteExisting: boolean;
  includeInvestments: boolean;
  includeCategories: boolean;
  includeTags: boolean;
}

const ImportExportPanel: React.FC<ImportExportPanelProps> = ({
  onImportComplete,
  onRefresh
}) => {
  const { isDark } = useTheme();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileValidation, setFileValidation] = useState<any>(null);
  const [importOptions, setImportOptions] = useState<ImportOptions>({
    overwriteExisting: false,
    includeInvestments: true,
    includeCategories: true,
    includeTags: true
  });

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Get current data from persistence
      const persistence = JSONPersistenceService.getInstance();
      const data = await persistence.loadData();
      
      if (!data) {
        throw new Error('No data to export');
      }

      // Use file persistence service to export
      const filePersistence = FilePersistenceService.getInstance();
      await filePersistence.exportToFile(data);
      
      showToast({
        title: 'Export Successful',
        message: 'Scenario data has been exported to your downloads folder',
        type: 'success'
      });
    } catch (error) {
      console.error('Export failed:', error);
      showToast({
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export data',
        type: 'error'
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const filePersistence = FilePersistenceService.getInstance();
      const validation = await filePersistence.validateFile(file);
      
      setSelectedFile(file);
      setFileValidation(validation);
      setShowImportDialog(true);
      
      if (!validation.isValid) {
        showToast({
          title: 'Invalid File',
          message: validation.errors.join(', '),
          type: 'error'
        });
      }
    } catch (error) {
      showToast({
        title: 'File Validation Failed',
        message: error instanceof Error ? error.message : 'Unknown error',
        type: 'error'
      });
    }
    
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleImport = async () => {
    if (!selectedFile || !fileValidation?.isValid) return;

    try {
      setIsImporting(true);
      
      const filePersistence = FilePersistenceService.getInstance();
      const persistence = JSONPersistenceService.getInstance();
      
      // Import data from file
      const importedData = await filePersistence.importFromFile(selectedFile);
      
      // Get existing data
      let existingData = await persistence.loadData();
      if (!existingData) {
        existingData = await persistence.initializeWithDefaults();
      }

      // Merge imported data with existing data
      const mergedData = await filePersistence.mergeImportedData(
        existingData,
        importedData,
        importOptions
      );

      // Save merged data
      await persistence.saveData(mergedData);

      const importedScenarios = importedData.scenarios?.length || 0;
      
      showToast({
        title: 'Import Successful',
        message: `Successfully imported ${importedScenarios} scenario(s)`,
        type: 'success'
      });

      // Notify parent component
      onImportComplete?.(importedScenarios);
      onRefresh?.();
      
      // Close dialog
      setShowImportDialog(false);
      setSelectedFile(null);
      setFileValidation(null);
      
    } catch (error) {
      console.error('Import failed:', error);
      showToast({
        title: 'Import Failed',
        message: error instanceof Error ? error.message : 'Failed to import data',
        type: 'error'
      });
    } finally {
      setIsImporting(false);
    }
  };


  return (
    <>
      <div className={`p-6 rounded-xl border ${
        isDark 
          ? 'bg-white/[0.02] border-white/10' 
          : 'bg-white border-gray-200 shadow-sm'
      }`}>
        <h3 className={`text-lg font-semibold mb-4 ${
          isDark ? 'text-white' : 'text-gray-900'
        }`}>
          Import & Export
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Export */}
          <button
            onClick={handleExport}
            disabled={isExporting}
            className={`flex items-center justify-center gap-3 p-4 rounded-lg border transition-all ${
              isDark
                ? 'border-white/20 hover:bg-white/[0.05] hover:border-white/30'
                : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            } ${isExporting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isExporting ? (
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <span className="text-2xl">📤</span>
            )}
            <div className="text-left">
              <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Export All Data
              </div>
              <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Download all scenarios & investments
              </div>
            </div>
          </button>

          {/* Import */}
          <button
            onClick={handleImportClick}
            disabled={isImporting}
            className={`flex items-center justify-center gap-3 p-4 rounded-lg border transition-all ${
              isDark
                ? 'border-white/20 hover:bg-white/[0.05] hover:border-white/30'
                : 'border-gray-200 hover:bg-gray-50 hover:border-gray-300'
            } ${isImporting ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span className="text-2xl">📥</span>
            <div className="text-left">
              <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Import Data
              </div>
              <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                Upload scenario data file
              </div>
            </div>
          </button>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Import Dialog */}
      <AnimatePresence>
        {showImportDialog && selectedFile && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={`w-full max-w-2xl max-h-[80vh] rounded-lg shadow-xl overflow-hidden ${
                isDark ? 'bg-navy-800' : 'bg-white'
              }`}
            >
              <div className={`p-6 border-b ${
                isDark ? 'border-white/10' : 'border-gray-200'
              }`}>
                <h3 className={`text-xl font-semibold ${
                  isDark ? 'text-white' : 'text-gray-900'
                }`}>
                  Import Scenario Data
                </h3>
                <p className={`text-sm mt-1 ${
                  isDark ? 'text-slate-400' : 'text-gray-600'
                }`}>
                  {selectedFile.name}
                </p>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {/* File validation results */}
                {fileValidation && (
                  <div className="mb-6">
                    {fileValidation.isValid ? (
                      <div className={`p-4 rounded-lg ${
                        isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-50 text-green-700'
                      }`}>
                        ✅ File validation passed
                        {fileValidation.summary && (
                          <div className="mt-2 text-sm">
                            <div>Scenarios: {fileValidation.summary.scenarios}</div>
                            <div>Investments: {fileValidation.summary.investments}</div>
                            <div>Version: {fileValidation.summary.version}</div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={`p-4 rounded-lg ${
                        isDark ? 'bg-red-500/20 text-red-400' : 'bg-red-50 text-red-700'
                      }`}>
                        ❌ File validation failed
                        <ul className="mt-2 text-sm list-disc list-inside">
                          {fileValidation.errors.map((error: string, index: number) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {fileValidation.warnings && fileValidation.warnings.length > 0 && (
                      <div className={`p-4 rounded-lg mt-2 ${
                        isDark ? 'bg-yellow-500/20 text-yellow-400' : 'bg-yellow-50 text-yellow-700'
                      }`}>
                        ⚠️ Warnings:
                        <ul className="mt-2 text-sm list-disc list-inside">
                          {fileValidation.warnings.map((warning: string, index: number) => (
                            <li key={index}>{warning}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}

                {/* Import options */}
                {fileValidation?.isValid && (
                  <div className="space-y-4">
                    <h4 className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      Import Options
                    </h4>
                    
                    <div className="space-y-3">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importOptions.overwriteExisting}
                          onChange={(e) => setImportOptions(prev => ({
                            ...prev,
                            overwriteExisting: e.target.checked
                          }))}
                          className="rounded"
                        />
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Overwrite Existing Scenarios
                          </div>
                          <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            Replace existing scenarios with imported versions
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importOptions.includeInvestments}
                          onChange={(e) => setImportOptions(prev => ({
                            ...prev,
                            includeInvestments: e.target.checked
                          }))}
                          className="rounded"
                        />
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Include Investment Library
                          </div>
                          <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            Import investment library data
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importOptions.includeCategories}
                          onChange={(e) => setImportOptions(prev => ({
                            ...prev,
                            includeCategories: e.target.checked
                          }))}
                          className="rounded"
                        />
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Include Categories
                          </div>
                          <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            Import category definitions
                          </div>
                        </div>
                      </label>

                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={importOptions.includeTags}
                          onChange={(e) => setImportOptions(prev => ({
                            ...prev,
                            includeTags: e.target.checked
                          }))}
                          className="rounded"
                        />
                        <div>
                          <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Include Tags
                          </div>
                          <div className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>
                            Import tag definitions
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              <div className={`p-6 border-t flex justify-end gap-3 ${
                isDark ? 'border-white/10' : 'border-gray-200'
              }`}>
                <button
                  onClick={() => {
                    setShowImportDialog(false);
                    setSelectedFile(null);
                    setFileValidation(null);
                  }}
                  className={`px-4 py-2 rounded font-medium transition-colors ${
                    isDark
                      ? 'text-slate-300 hover:text-white hover:bg-white/[0.05]'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleImport}
                  disabled={!fileValidation?.isValid || isImporting}
                  className={`px-4 py-2 rounded font-medium transition-colors flex items-center gap-2 ${
                    fileValidation?.isValid && !isImporting
                      ? isDark
                        ? 'bg-professional-blue-600 text-white hover:bg-professional-blue-500'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isImporting && (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {isImporting ? 'Importing...' : 'Import'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ImportExportPanel;